import {
  Alert,
  Grid2 as Grid,
  Snackbar,
  SnackbarCloseReason,
  Stack as MuiStack,
  Typography,
} from '@mui/material';
import { ControlModes, Stack, StackEdited } from '@src/types/Stacks';
import TemplatesCard from '@src/components/Home/Config/ConfigurableModes/components/TemplatesCard';
import useStackMutations from '@src/hooks/api/mutations/useStackMutations';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import { stacksViewSx } from '@src/components/_shared/sxStyles/Config/stacks';
import useSendControllersConfig from '@src/hooks/api/mutations/useSendControllersConfig';
import storeConnector from '@src/store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import { ControlMode } from '@src/types/control-modes/ControlModes';
import useStackValidation from '@src/hooks/useStackValidation';
import ImportErrorDialog from '../ConfigurableModes/components/ImportErrorDialog';

function StackView({
  stack,
  isDefault,
  siteMeta,
}: { stack: Stack; siteMeta: SiteMeta; isDefault?: boolean }) {
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const { updateStack } = useStackMutations(stack?.id, isDefault);
  const sendControllerConfig = useSendControllersConfig();
  const queryClient = useQueryClient();
  const {
    validateTemplate,
    error: stackValidationError,
    resetError,
  } = useStackValidation();

  const handleUpdateStack = async (controlMode: ControlModes) => {
    const updatedStack = {
      name: stack.name,
      controlModes: stack.controlModes?.map((mode) => {
        if (mode.name === controlMode.name) {
          return controlMode;
        }
        return mode;
      }),
    };
    const controlModeBody = controlMode.sections.reduce(
      (acc: Record<string, Record<string, string>>, section) => {
        acc[section.controllerField] = {
          ...acc[section.controllerField],
          ...section.fields.reduce((acc: Record<string, string>, field) => {
            acc[field.controllerField] = String(field.value);
            return acc;
          }, {}),
        };
        return acc;
      },
      {},
    );

    // Reset any previous errors
    resetError();

    // Validate the stack before sending to API
    const isValid = await validateTemplate([updatedStack]);

    if (!isValid) {
      // If validation failed, show the error dialog with detailed validation errors
      setErrorDialogOpen(true);
      return;
    }

    sendControllerConfig.mutate({
      configBody: controlModeBody,
      modeName: controlMode.name as ControlMode,
      siteId: siteMeta.siteId,
    });

    updateStack.mutate(updatedStack as StackEdited, {
      onSuccess: () => {
        if (isDefault) {
          queryClient.invalidateQueries({
            queryKey: [QueryKeys['default-stack']],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.stacks],
          });
        }
        setConfirmOpen(true);
      },
      onError: (error: Error) => {
        setError(error.message);
        setConfirmOpen(true);
      },
    });
  };

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    resetError();
  };

  return (
    <div>
      <ImportErrorDialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        error={stackValidationError}
      />
      <Snackbar
        open={confirmOpen}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={error ? 'error' : 'success'}
          variant='filled'
        >
          {error ? error : 'Template updated successfully!'}
        </Alert>
      </Snackbar>
      <section>
        <MuiStack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Typography sx={stacksViewSx.view.titles.control}>
                Control Mode
              </Typography>
            </Grid>
            <Grid size={8}>
              <Typography sx={stacksViewSx.view.titles.stack}>
                {stack.name}
              </Typography>
            </Grid>
          </Grid>

          {stack.controlModes?.map((controlMode, index: number) => (
            <TemplatesCard
              key={index}
              controlMode={controlMode}
              handleUpdateStack={handleUpdateStack}
              isDefault={isDefault}
            />
          ))}
          {!stack.controlModes?.length && (
            <Typography sx={stacksViewSx.view.titles.noModes}>
              No modes to display
            </Typography>
          )}
        </MuiStack>
      </section>
    </div>
  );
}

export default storeConnector(StackView, {
  config: ['siteMeta'],
});
